import { KeyboardEvent, useCallback, useRef } from 'react'

type RovingOrientation = 'vertical' | 'horizontal' | 'grid'

interface UseRovingTabIndexOptions {
    // selector for the focusable items inside the container (matched against the
    // focused element itself, so nested controls like a kebab keep their own keys)
    itemSelector: string
    orientation?: RovingOrientation
    // 1D: wrap from last back to first (and vice versa). grid: applies to Left/Right only,
    // so Right on the end of a row continues onto the next row. Up/Down never wrap.
    wrap?: boolean
    // fired after focus has moved to a different item, e.g. to select it (automatic activation)
    onFocusItem?: (item: HTMLElement, index: number) => void
}

/**
 * Keyboard movement for a roving-tabindex composite (tablist, listbox-ish list, or card grid).
 *
 * The hook only moves focus; the consumer owns which item currently carries tabIndex 0 (the
 * selected tab, the last-focused card, ...) and renders the rest with tabIndex -1 so the whole
 * composite is a single Tab stop.
 *
 * For `grid`, attach `containerRef` to the CSS grid element itself: the column count is read
 * from its computed `grid-template-columns` on every key press, so `auto-fill` layouts stay
 * correct across resizes without a listener.
 */
function useRovingTabIndex<T extends HTMLElement = HTMLDivElement>({
    itemSelector,
    orientation = 'vertical',
    wrap = true,
    onFocusItem
}: UseRovingTabIndexOptions) {

    const containerRef = useRef<T>(null)

    const getItems = useCallback((): HTMLElement[] =>
        Array.from(containerRef.current?.querySelectorAll<HTMLElement>(itemSelector) ?? []),
    [itemSelector])

    const getColumnCount = useCallback((): number => {
        if (!containerRef.current) return 1

        // computed value is the resolved track list, e.g. "170px 170px 170px"
        const tracks = getComputedStyle(containerRef.current).gridTemplateColumns
        if (!tracks || tracks === 'none') return 1

        return tracks.split(' ').length
    }, [])

    const onKeyDown = useCallback((e: KeyboardEvent) => {
        const items = getItems()
        const currentIndex = items.indexOf(e.target as HTMLElement)
        if (currentIndex === -1) return

        const last = items.length - 1

        const step = (delta: number, wrapAround: boolean) => {
            const raw = currentIndex + delta
            if (wrapAround) return (raw + items.length) % items.length
            return Math.min(Math.max(raw, 0), last)
        }

        const stepRow = (direction: -1 | 1) => {
            const columns = getColumnCount()
            const raw = currentIndex + direction * columns
            if (raw < 0) return currentIndex
            if (raw <= last) return raw

            // moving down out of a full row into a shorter final row lands on the last item;
            // already on the final row means there is nowhere to go
            const currentRow = Math.floor(currentIndex / columns)
            const lastRow = Math.floor(last / columns)
            return currentRow < lastRow ? last : currentIndex
        }

        let nextIndex: number | undefined

        switch (e.key) {
            case 'Home':
                nextIndex = 0
                break
            case 'End':
                nextIndex = last
                break
            case 'ArrowUp':
                if (orientation === 'vertical') nextIndex = step(-1, wrap)
                if (orientation === 'grid') nextIndex = stepRow(-1)
                break
            case 'ArrowDown':
                if (orientation === 'vertical') nextIndex = step(1, wrap)
                if (orientation === 'grid') nextIndex = stepRow(1)
                break
            case 'ArrowLeft':
                if (orientation !== 'vertical') nextIndex = step(-1, wrap)
                break
            case 'ArrowRight':
                if (orientation !== 'vertical') nextIndex = step(1, wrap)
                break
        }

        if (nextIndex === undefined) return

        // handled even when we stay put, so an edge press doesn't scroll the page
        e.preventDefault()
        if (nextIndex === currentIndex) return

        const next = items[nextIndex]
        next.focus()
        onFocusItem?.(next, nextIndex)
    }, [getItems, getColumnCount, orientation, wrap, onFocusItem])

    return { containerRef, onKeyDown }
}

export { useRovingTabIndex }
export type { RovingOrientation, UseRovingTabIndexOptions }
